import React from 'react';
import { CircularProgress, Stack, Typography, styled } from '@mui/material';
import { LibraryCard } from 'components/LibraryCard/LibraryCard';
import { useInfiniteScroll } from 'hooks/useInfiniteScroll';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { useLoggedInUser } from 'features/auth/hooks/useAuthData';
import { useContentSearch } from 'features/resources/hooks';
import { getArticleImageForUser } from 'features/resources/utils';
import { ContentSearchItemAPIResponse, TransformedLibraryArticle } from 'features/resources/types';
import { ResourceSearchErrorState } from './ResourceSearchErrorState';
import { ResourceSearchEmptyState } from './ResourceSearchEmptyState';
import { ResourceCardGrid, ResourceCardGridCell } from './ResourceCardGrid';

const CenteredSpinner = styled(Stack)(({ theme }) => ({
  width: '100%',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(8, 0),
}));

const InfiniteScrollContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  padding: theme.spacing(2, 0),
}));

/** Maps a search result item to the shared article shape. */
const toTransformedArticle = (
  item: ContentSearchItemAPIResponse,
  memberUuid: string,
): TransformedLibraryArticle => ({
  id: item.id,
  title: item.title,
  description: item.description ?? '',
  contentUrl: item.url,
  contentType: item.content_type,
  // Keyed by url to match the library and favorites cards for the same article.
  imageUrl: item.thumbnail_url ?? getArticleImageForUser(memberUuid, item.url),
});

export interface ContentSearchResultsProps {
  /** Already trimmed/debounced; empty string means no active search. */
  searchTerm: string;
  onArticleClick?: (article: TransformedLibraryArticle) => void;
  isArticleFavorited?: (article: TransformedLibraryArticle) => boolean;
  onToggleFavorite?: (article: TransformedLibraryArticle) => void;
}

/** Shared search results renderer for the tablet/desktop inline search and the mobile search page. */
export const ContentSearchResults = ({
  searchTerm,
  onArticleClick,
  isArticleFavorited,
  onToggleFavorite,
}: ContentSearchResultsProps) => {
  const user = useLoggedInUser();
  const { translation } = useTranslationContext();

  const {
    contentResults,
    isLoading: isSearchLoading,
    isFetching: isSearchFetching,
    error: searchError,
    careProgramsHasMore,
    onDemandHasMore,
    loadNextPage,
  } = useContentSearch(searchTerm);

  const hasMore = careProgramsHasMore || onDemandHasMore;

  const sentinelRef = useInfiniteScroll(loadNextPage);

  const renderResultCard = (keyPrefix: string, item: ContentSearchItemAPIResponse) => {
    const article = toTransformedArticle(item, user?.uuid ?? '');
    return (
      <ResourceCardGridCell key={`${keyPrefix}-${item.id}`}>
        <LibraryCard
          title={article.title}
          imageUrl={article.imageUrl}
          onClick={() => onArticleClick?.(article)}
          ariaLabel={`${article.title} article`}
          showFavorite
          isFavorited={isArticleFavorited?.(article)}
          onToggleFavorite={() => onToggleFavorite?.(article)}
        />
      </ResourceCardGridCell>
    );
  };

  if (searchError) {
    return <ResourceSearchErrorState />;
  }

  if (isSearchLoading) {
    return (
      <CenteredSpinner>
        <CircularProgress size={64} thickness={3} />
        <Typography variant="callout" color="grey.800">
          {translation('loading_search_results')}
        </Typography>
      </CenteredSpinner>
    );
  }

  // Debounce window: term hasn't been committed to the API call yet.
  if (!searchTerm) {
    return null;
  }

  // care_programs and on_demand are both plain content items - combined into one flat results list.
  const { care_programs: carePrograms, on_demand: onDemand } = contentResults;
  const resultCount = carePrograms.length + onDemand.length;
  if (resultCount === 0) {
    return <ResourceSearchEmptyState />;
  }

  return (
    <>
      <Typography variant="callout" color="grey.700">
        {translation('search_results')} ({resultCount})
      </Typography>
      <ResourceCardGrid>
        {carePrograms.map((item) => renderResultCard('care-program', item))}
        {onDemand.map((item) => renderResultCard('on-demand', item))}
      </ResourceCardGrid>
      {hasMore && (
        <InfiniteScrollContainer ref={sentinelRef}>
          {isSearchFetching && <CircularProgress size={28} />}
        </InfiniteScrollContainer>
      )}
    </>
  );
};
